import { Link } from "@tanstack/react-router";
import { FunnelIcon, MapPinIcon, PlusCircleIcon } from "@heroicons/react/24/outline";
import { BarsArrowDownIcon } from "@heroicons/react/24/outline";
import { parsePhoneNumber } from "awesome-phonenumber";
import Article from "#/components/article";
import Breadcrumbs from "#/components/breadcrumbs";
import Footer from "#/components/footer/footer";
import CreateJobAlert from "#/components/jobs/CreateJobAlert";
import JobPostingsList from "#/components/jobs/JobsList";
import HiringOrgHeader from "#/components/jobs/HiringOrgHeader";
import LocumJobs from "#/components/locumJob";
import MDXRemote from "#/components/MdxRemote";
import NavBar from "#/components/navbar/navbar";
import SearchBar from "#/components/searchbar";
import SimilarJobsList from "#/components/job_posting/SimilarJobsList";
import ApplyBtn from "#/components/job_posting/ApplyBtn";
import EmployerAbout from "#/components/job_posting/EmployerAbout";
import JobList from "#/components/jobList";
import JobLocation from "#/components/job_posting/JobLocation";
import type { Facility, RoamdocJobPost } from "#/db/schema";
import { toAppPath } from "#/lib/router-links";
import { taxonomyById } from "#/lib/taxonomies/taxonomy_tools";
import type { HiringOrg, Image } from "#/types/types";
import type { CatchAllPageData } from "#/features/slug-routes/catch-all.functions";

export default function CatchAllPage({ data }: { data: CatchAllPageData }) {
  switch (data.kind) {
    case "article":
      return (
        <>
          <Article data={data.article} />
          <JsonLdScript jsonLd={data.jsonLd} />
        </>
      );
    case "anesthesiologist-jobs-location":
      return <AnesthesiologistJobsLocationPage data={data} />;
    case "jobs-location":
      return <JobsLocationPage data={data} />;
    case "jobs-at-hiring-org":
      return <JobsAtHiringOrgPage data={data} />;
    case "job-posting":
      return <JobPostingPage data={data} />;
  }
}

function AnesthesiologistJobsLocationPage({
  data,
}: {
  data: Extract<CatchAllPageData, { kind: "anesthesiologist-jobs-location" }>;
}) {
  const { city } = data;
  const stateCities = city.stateCities.map((stateCity, index) => {
    const text =
      [
        `Hiring Anesthesiologists in ${stateCity.name}, ${stateCity.stateCode}`,
        `Anesthesiologist vacancies in ${stateCity.name}, ${stateCity.stateCode}`,
        `Open Anesthesiologist positions in ${stateCity.name}, ${stateCity.stateCode}`,
        `Anesthesiologist employment in ${stateCity.name}, ${stateCity.stateCode}`,
        `Anesthesiologist openings in ${stateCity.name}, ${stateCity.stateCode}`,
        `Anesthesiologists needed in ${stateCity.name}, ${stateCity.stateCode}`,
      ][index] ??
      `Anesthesiologist jobs in ${stateCity.name}, ${stateCity.stateCode}`;

    return (
      <li key={stateCity.name} className="pt-5">
        <Link {...toAppPath(`/anesthesiologist-jobs-in-${stateCity.slug}`)}>
          <h3>{text}</h3>
        </Link>
        <MDXRemote source={stateCity.description} />
      </li>
    );
  });

  const regionalCities = city.regionalCities.map((regionalCity) => (
    <li
      key={regionalCity.name}
      className="mb-2 border-b border-gray-200 pb-4 md:mb-4"
    >
      <h3 className="text-lg font-semibold text-gray-800">{regionalCity.name}</h3>
      <div className="text-sm text-gray-600">
        <MDXRemote source={regionalCity.description} />
      </div>

      <button
        type="button"
        role="button"
        aria-controls="job-listings"
        aria-pressed="false"
        title={`Only show job listings for ${regionalCity.name}`}
        aria-label={`Only show job listings for ${regionalCity.name}`}
        className="group relative mt-2 flex justify-center rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2 disabled:bg-gray-200 md:px-4 md:py-2 md:text-sm"
      >
        <span className="absolute inset-y-0 left-0 flex items-center pl-3">
          <FunnelIcon className="h-4 w-4 text-gray-500 md:h-5 md:w-5" aria-hidden="true" />
        </span>
        <span className="pl-8">{regionalCity.name} Jobs</span>
      </button>
    </li>
  ));

  return (
    <>
      <header>
        <NavBar />
        <nav
          aria-label="Breadcrumb"
          className="mx-auto max-w-screen-xl text-sm md:text-sm"
          itemScope
          itemType="https://schema.org/BreadcrumbList"
        >
          <ol className="flex list-none p-3 text-xs text-gray-400 md:p-4 md:text-sm">
            <li
              className="flex shrink items-center"
              itemProp="itemListElement"
              itemScope
              itemType="https://schema.org/ListItem"
            >
              <Link
                className="flex shrink hover:text-sky-500"
                itemProp="item"
                to="/anesthesiology-job-search"
              >
                <span itemProp="name">
                  <span>Anesthesiology</span>{" "}
                  <span className="whitespace-nowrap">Job Search</span>
                </span>
              </Link>
              <meta itemProp="position" content="1" />
              <span className="mx-2 text-gray-400 md:mx-2">/</span>
            </li>
            <li
              className="flex shrink items-center"
              itemProp="itemListElement"
              itemScope
              itemType="https://schema.org/ListItem"
            >
              <span itemProp="name">
                <span>Anesthesiologist </span>
                <span className="whitespace-nowrap">
                  {`in ${city.name}, ${city.stateCode}`}
                </span>
              </span>
              <meta itemProp="position" content="2" />
            </li>
          </ol>
        </nav>
      </header>

      <main id="content" className="mx-auto mt-10 max-w-3xl space-y-10">
        <h1 className="text-center text-5xl">
          <span>Search Anesthesiologist Jobs</span>
          <span className="whitespace-nowrap">
            {` in ${city.name}, ${city.stateCode}`}
          </span>
        </h1>

        <SearchBar value={`${city.name}, ${city.stateCode}`} />

        <div className="mx-8 space-y-5">
          <MDXRemote source={city.anesthesiologistContent?.intro} />
        </div>

        <section className="mx-8 space-y-5">
          <h2 className="text-3xl">{`Locum Anesthesiologist jobs near ${city.name}`}</h2>

          <LocumJobs postings={city.anesthesiologistJobPostings} />

          <button className="block w-full rounded bg-blue-500 px-4 py-2 text-center text-white no-underline hover:bg-blue-600 hover:text-white focus:outline-none focus:ring-2 focus:ring-blue-400">
            {`View more locum anesthesiologist positions near ${city.name} →`}
          </button>
        </section>

        <div
          id="job-listings"
          className="mx-8 space-y-5"
          itemType="http://schema.org/ItemList"
          itemScope
        >
          <JobList postings={city.anesthesiologistJobPostings} />
        </div>

        <section className="mx-8 space-y-5">
          <h2 className="text-3xl">Filter by nearby cities</h2>

          <div className="container mx-auto max-w-6xl px-0 md:px-4">
            <ul className="grid list-none grid-cols-1 gap-4 pl-0 md:grid-cols-2 lg:grid-cols-3">
              {regionalCities}
            </ul>
          </div>
        </section>

        <section className="mx-8 space-y-5">
          <CreateJobAlert />
        </section>

        <aside className="mx-8 space-y-5">
          <h2 className="text-3xl">
            {city.rank === 1 && city.stateCities.length > 0
              ? `More anesthesia physician jobs in ${city.state}`
              : "More Locations Seeking Anesthesia Physicians"}
          </h2>

          <ul className="divide-y divide-gray-200">
            {city.stateCities.length > 0 ? stateCities : null}

            {city.stateLink ? (
              <li className="pt-5">
                <h3>
                  <Link {...toAppPath(`/anesthesiologist-jobs-in-${city.stateLink.slug}`)}>
                    Search Anesthesiologist jobs in {city.stateLink.state}
                  </Link>
                </h3>
              </li>
            ) : null}
          </ul>
        </aside>

        {city.rank === 1 ? (
          <>
            <section className="mx-8 space-y-5">
              <h2 className="text-3xl">
                Salary range in {city.state} for Anesthesiologists
              </h2>

              <p>
                The average Anesthesiologist salary in {city.state} is{" "}
                <strong>{formatAsCurrency(city.anesthesiologistSalary.avg)}</strong>. The
                range usually falls between{" "}
                <strong>{formatAsCurrency(city.anesthesiologistSalary.min)}</strong> and{" "}
                <strong>{formatAsCurrency(city.anesthesiologistSalary.max)}</strong>.
              </p>

              <p>
                Factors affecting the compensation of Anesthesiologists include their
                professional experience, areas of specialization, geographical
                location of practice, and whether they work in a hospital, private
                practice, or academic setting and whether they practice in a rural
                or urban environment.
              </p>
            </section>

            <section className="mx-8 space-y-5">
              <h2 className="text-3xl">
                Medical license to practice in the state of {city.state}
              </h2>

              <MDXRemote source={city.anesthesiologistContent?.medicalLicense} />
            </section>
          </>
        ) : null}

        <div id="job-details-drawer"></div>
      </main>
      <Footer fmBio />
    </>
  );
}

function JobsLocationPage({
  data,
}: {
  data: Extract<CatchAllPageData, { kind: "jobs-location" }>;
}) {
  const [roleSegment, location] = parseJobsLocationSlug(data.slug);
  const role = capitalizeWords(roleSegment.replaceAll("-", " "));
  const { cityName, stateId } = parseLocation(location);
  const breadcrumbs = buildJobsLocationBreadcrumbs(role, cityName, stateId);
  const links = data.slug.includes("physician")
    ? [
        { href: "/physician-salary", text: "Physician Salary Guide" },
        { href: "/physician-cv-examples", text: "Resume CV Examples" },
      ]
    : [
        { href: "/nurse-salary", text: "Nurse Salary Guide" },
        { href: "/nurse-resume-examples", text: "Resume Examples" },
      ];

  return (
    <>
      <header>
        <NavBar overrideLinks={links} />
        <Breadcrumbs first={breadcrumbs.firstCrumb} second={breadcrumbs.secondCrumb} />
      </header>
      <main className="mx-auto mt-10 max-w-3xl space-y-10">
        <h1 className="text-center text-5xl">
          <span>Search {role} jobs</span>
          <span className="whitespace-nowrap">{` in ${cityName}, ${stateId}`}</span>
        </h1>

        <SearchBar value={`${cityName}, ${stateId}`} />

        <section>
          <h2 className="mb-5 pl-16 text-gray-400">
            {`List of ${data.jobPostsCount} ${role} job postings near ${cityName}`}
          </h2>
          <div
            id="job-listings"
            className="mx-8 space-y-5"
            itemType="http://schema.org/ItemList"
            itemScope
          >
            <JobPostingsList postings={data.jobPosts} />
          </div>

          <div className="mt-10 flex">
            <button
              type="button"
              className="flex w-full items-center justify-center space-x-2 rounded-lg bg-sky-600 px-4 py-4 text-xl text-white shadow-lg hover:bg-sky-700"
              role="dialog"
              aria-modal="true"
              aria-labelledby="Compare job posting"
            >
              <BarsArrowDownIcon className="h-6 w-6" />
              <span>View more jobs</span>
            </button>
          </div>
        </section>

        <section className="pt-20">
          <h2 className="text-3xl">{`Hospitals hiring ${role}s near ${cityName}`}</h2>
          <FacilitiesHiringList jobPosts={data.jobPosts} />
        </section>

        <div className="pt-20">
          <CreateJobAlert />
        </div>
        <div id="job-details-drawer"></div>
      </main>
      <Footer />
    </>
  );
}

function JobsAtHiringOrgPage({
  data,
}: {
  data: Extract<CatchAllPageData, { kind: "jobs-at-hiring-org" }>;
}) {
  const coverImage = getImage(data.hiringOrg, "COVER");
  const logoImage = getImage(data.hiringOrg, "LOGO");
  const breadcrumbs = buildJobsAtBreadcrumbs(data.hiringOrg);
  const links = [
    { href: "/physician-salary-in-united-states", text: "Physician Salary Guide" },
    { href: "/physician-cv-examples", text: "Resume CV Examples" },
    { href: "/nurse-salary-in-united-states", text: "Nurse Salary Guide" },
  ];

  return (
    <>
      <header>
        <NavBar overrideLinks={links} />
        <Breadcrumbs first={breadcrumbs.firstCrumb} second={breadcrumbs.secondCrumb} />
      </header>
      <main className="mx-auto mt-10 max-w-2xl space-y-5 text-gray-900">
        <header className="pb-5">
          <HospitalProfileHeader
            coverImage={coverImage}
            logoImage={logoImage}
            title={data.hiringOrg.metaTitle ?? data.hiringOrg.name}
          />
        </header>

        <SearchBar value="" />

        <section>
          <h2 className="mx-8 mb-5 text-xl text-sky-600">Job Postings</h2>

          <SimilarJobsList postings={data.jobPostings} />
        </section>

        <section className="mx-8 pt-20">
          <h2 className="mb-5 text-xl text-sky-600">About {data.hiringOrg.name}</h2>

          <div className="mb-10">
            <MDXRemote source={data.hiringOrg.about} />
          </div>

          {data.hiringOrg.youtubeVideoId?.trim().length ? (
            <YoutubeVideoSection videoId={data.hiringOrg.youtubeVideoId} />
          ) : null}
        </section>

        <section className="mx-8 pt-20">
          <h2 className="mb-5 text-xl text-sky-600">
            Facility Locations in {data.hiringOrg.stateName}
          </h2>
          <FacilityList facilityList={data.facilities} />
        </section>

        <div className="mx-8 pt-20">
          <CreateJobAlert />
        </div>
      </main>
      <Footer />
      <JsonLdScript jsonLd={data.jsonLd} />
    </>
  );
}

function JobPostingPage({
  data,
}: {
  data: Extract<CatchAllPageData, { kind: "job-posting" }>;
}) {
  const taxonomy = taxonomyNameFromPosting(data.posting.primaryRoleTaxonomyId);
  const breadcrumbs = buildJobPostingBreadcrumbs(data.slug, taxonomy);
  const links = data.posting.slug?.includes("physician")
    ? [
        { href: "/physician-salary-in-united-states", text: "Physician Salary Guide" },
        { href: "/physician-cv-examples", text: "Resume CV Examples" },
      ]
    : [
        { href: "/nurse-salary-in-united-states", text: "Nurse Salary Guide" },
        { href: "/nurse-resume-examples", text: "Resume Examples" },
      ];

  const coverImage = data.hiringOrg ? getImage(data.hiringOrg, "COVER") : null;
  const logoImage = data.hiringOrg ? getImage(data.hiringOrg, "LOGO") : null;

  return (
    <>
      <header>
        <NavBar overrideLinks={links} />
        <Breadcrumbs first={breadcrumbs.firstCrumb} second={breadcrumbs.secondCrumb} />
      </header>
      <main
        id="job-posting"
        className="mx-auto mt-5 max-w-2xl space-y-5 text-gray-900"
      >
        <article>
          <header>
            <HiringOrgHeader
              title={data.posting.title ?? ""}
              coverImage={coverImage}
              logoImage={logoImage}
            />
          </header>

          <section className="mx-5 pt-10">
            <DatePosted datePosted={data.posting.schema?.datePosted} />

            <h2 className="mb-5 text-xl text-sky-600">Job Posting Description</h2>

            <p className="mb-3">{data.posting.mirror?.title}</p>

            <div className="job-posting-description">
              <MDXRemote source={data.posting.description ?? ""} />
            </div>

            <div className="mt-10 flex">
              <button
                type="button"
                className="flex w-full items-center justify-center space-x-2 rounded-lg bg-green-600 px-4 py-4 text-xl text-white shadow-lg hover:bg-green-700"
                role="dialog"
                aria-modal="true"
                aria-labelledby="Compare job posting"
              >
                <PlusCircleIcon className="h-6 w-6" />
                <span>Compare to other Job Offers</span>
              </button>
            </div>

            <TypeOfEmployment employmentType={data.posting.schema?.employmentType} />

            <JobLocation posting={data.posting} />
          </section>

          <EmployerAbout hiringOrg={data.hiringOrg} />

          <div className="mx-8 mt-10 flex flex-col">
            <ApplyBtn jobPostId={data.posting.id} />

            <div className="mt-3 flex space-x-2">
              <button
                type="button"
                className="grow rounded-lg bg-sky-400 px-4 py-2 text-center text-sm font-medium text-white hover:bg-sky-500 focus:outline-none focus:ring-4 focus:ring-blue-500"
                role="dialog"
                aria-modal="true"
                aria-labelledby="Save job posting"
              >
                Save
              </button>
              <button
                type="button"
                className="grow rounded-lg bg-sky-400 px-4 py-2 text-center text-sm font-medium text-white hover:bg-sky-500 focus:outline-none focus:ring-4 focus:ring-blue-500"
                role="dialog"
                aria-modal="true"
                aria-labelledby="Share job posting"
              >
                Share
              </button>
            </div>
          </div>
        </article>

        <div className="mx-5 pt-16">
          <CreateJobAlert />
        </div>

        {data.similarJobs.length ? (
          <aside className="space-y-3 pt-10">
            <h2 className="mx-5 mb-5 mt-10 text-xl text-sky-600">
              Similar Nearby Job Postings
            </h2>
            <SimilarJobsList postings={data.similarJobs} />
          </aside>
        ) : null}
      </main>
      <Footer />
      <JsonLdScript jsonLd={data.jsonLd} />
    </>
  );
}

function JsonLdScript({ jsonLd }: { jsonLd: string | null }) {
  if (!jsonLd) {
    return null;
  }

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: jsonLd }}
    />
  );
}

function YoutubeVideoSection({ videoId }: { videoId: string }) {
  return (
    <div>
      <iframe
        style={{ aspectRatio: "16 / 9", width: "100%" }}
        src={`https://www.youtube.com/embed/${videoId}`}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      ></iframe>
    </div>
  );
}

function FacilityList({ facilityList }: { facilityList: Facility[] | null }) {
  if (!facilityList?.length) {
    return null;
  }

  return (
    <div className="container mx-auto max-w-6xl px-0 md:px-4">
      <ul className="grid list-none grid-cols-1 gap-4 pl-0 md:grid-cols-2 lg:grid-cols-2">
        {facilityList.map((facility) => (
          <li key={facility.id} className="mb-10">
            <article>
              <h3 className="mb-2">{facility.name}</h3>
              <address className="space-y-1 pl-5">
                <p>{facility.streetAddress}</p>
                <p>
                  {facility.cityName}, {facility.stateId} {facility.postalCode}
                </p>
                {facility.phone ? (
                  <p>
                    <a
                      href={
                        parsePhoneNumber(facility.phone, { regionCode: "US" }).number
                          ?.rfc3966
                      }
                    >
                      {
                        parsePhoneNumber(facility.phone, { regionCode: "US" }).number
                          ?.national
                      }
                    </a>
                  </p>
                ) : null}
              </address>

              <div className="mt-2">
                {facility.googleMapsUrl ? (
                  <Link
                    className="flex rounded-lg bg-white px-4 py-2 text-center text-sm font-medium focus:outline-none focus:ring-4 focus:ring-gray-200"
                    to={facility.googleMapsUrl}
                    target="_blank"
                  >
                    <MapPinIcon className="h-6 w-6" />
                    Map
                  </Link>
                ) : null}
              </div>
            </article>
          </li>
        ))}
      </ul>
    </div>
  );
}

function FacilitiesHiringList({ jobPosts }: { jobPosts: RoamdocJobPost[] }) {
  const facilities = jobPosts.map((jobPost) => jobPost.facility);
  const uniqueFacilities = Array.from(
    new Set(facilities.map((facility) => facility?.id)),
  ).map((id) => facilities.find((facility) => facility?.id === id));

  return (
    <div className="grid list-none grid-cols-1 gap-4 pl-0 pt-10 md:grid-cols-2 lg:grid-cols-2">
      {uniqueFacilities.map((facility) =>
        facility ? (
          <article key={facility.id} className="flex flex-col pb-10">
            <h3>{facility.name}</h3>
            <address className="space-y-1 pl-3 pt-1">
              <p>{facility.streetAddress}</p>
              <p>
                {facility.cityName}, {facility.stateId} {facility.postalCode}
              </p>
            </address>
          </article>
        ) : null,
      )}
    </div>
  );
}

function HospitalProfileHeader({
  coverImage,
  logoImage,
  title,
}: {
  coverImage: Image | null;
  logoImage: Image | null;
  title: string | null;
}) {
  return (
    <div className="relative">
      <div className="h-64 w-full bg-gray-200">
        <img
          src={coverImage?.url}
          alt={coverImage?.alt}
          className="h-full w-full object-cover"
        />
      </div>
      <div className="absolute bottom-0 left-0 right-0 top-0 flex items-end bg-gradient-to-t from-black to-transparent p-4">
        <div className="flex items-center">
          <div className="aspect-square h-24 w-24 min-w-24 overflow-hidden rounded-full border-4 border-white bg-white shadow-lg">
            <img
              src={logoImage?.url}
              alt={logoImage?.alt}
              className="h-full w-full object-cover"
            />
          </div>
          <div className="ml-4 flex text-white">
            <h1 className="text-wrap text-2xl font-bold">{title}</h1>
          </div>
        </div>
      </div>
    </div>
  );
}

function DatePosted({ datePosted }: { datePosted: string | undefined }) {
  if (!datePosted) {
    return null;
  }

  const date = new Date(datePosted).toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  return <p className="text-sm text-gray-500">Posted {date}</p>;
}

function TypeOfEmployment({
  employmentType,
}: {
  employmentType: string | string[] | undefined;
}) {
  const effectiveEmploymentType = Array.isArray(employmentType)
    ? employmentType[0]
    : employmentType;

  if (!effectiveEmploymentType) {
    return null;
  }

  const labels: Record<string, string> = {
    CONTRACTOR: "Contractor",
    FULL_TIME: "Full Time",
    INTERN: "Intern",
    OTHER: "Other",
    PART_TIME: "Part Time",
    PER_DIEM: "Per Diem",
    TEMPORARY: "Temporary",
    VOLUNTEER: "Volunteer",
  };

  return (
    <div className="mt-10">
      <h3 className="font-normal">
        Employment Type: {labels[effectiveEmploymentType] ?? effectiveEmploymentType}
      </h3>
    </div>
  );
}

function buildJobsLocationBreadcrumbs(role: string, cityName: string, stateId: string) {
  return {
    firstCrumb: {
      href: `/${role.toLowerCase().replaceAll(" ", "-")}-job-search`,
      textOne: role,
      textTwo: "job search",
    },
    secondCrumb: {
      textOne: `${role} jobs`,
      textTwo: `in ${cityName}, ${stateId}`,
    },
  };
}

function buildJobsAtBreadcrumbs(hiringOrg: { name: string }) {
  return {
    firstCrumb: {
      href: "/healthcare-job-search",
      textOne: "Healthcare",
      textTwo: "Job Search",
    },
    secondCrumb: {
      textOne: hiringOrg.name,
      textTwo: `in ${hiringOrg.name}`,
    },
  };
}

function buildJobPostingBreadcrumbs(slug: string, primaryRoleName: string) {
  const segments = slug.split("/");
  const [roleSegment, location] = (segments[0]?.split("-jobs-in-") as [string, string]) ?? [
    "",
    "",
  ];
  const role = capitalizeWords(roleSegment.replaceAll("-", " "));
  const { cityName, stateId } = parseLocation(location);

  return {
    firstCrumb: {
      href: `/${segments[0]}`,
      textOne: `${role} jobs in`,
      textTwo: `${cityName}, ${stateId}`,
    },
    secondCrumb: {
      textOne: primaryRoleName,
      textTwo: "job posting",
    },
  };
}

function taxonomyNameFromPosting(primaryRoleTaxonomyId: number) {
  return taxonomyById(primaryRoleTaxonomyId)?.name ?? "Healthcare";
}

function parseJobsLocationSlug(slug: string) {
  return (slug.split("-jobs-in-") as [string, string]) ?? ["", ""];
}

function parseLocation(slug: string) {
  const parts = slug.split("-");
  const stateId = parts.pop()?.toUpperCase() ?? "";
  const cityName = parts
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

  return { cityName, stateId };
}

function capitalizeWords(input: string) {
  return input
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function formatAsCurrency(number: number, currencyCode = "USD") {
  return number.toLocaleString("en-US", {
    currency: currencyCode,
    minimumFractionDigits: 0,
    style: "currency",
  });
}

function getImage(hiringOrg: HiringOrg, imageType: Image["imageType"]) {
  return hiringOrg.images.find((image) => image.imageType === imageType) ?? null;
}
